import { useCloseDeletedSelection } from "../../sites/components/common/useCloseDeletedSelection";
import { useEffect, useRef, useState } from "react";
import { SystemAdminService } from "../../services/SystemAdminService";
import Loading from "../../sites/components/common/Loading";
import { FormControlElement, IComponent, IComponentPropertyType, IComponentProperty } from "../../models/Interfaces";
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Alert, Button, Col, Form, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faSortDown, faSortUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import AppModal from "../../sites/components/common/AppModal";
import { useConfirm } from "../../config/ConfirmProvider";
import { Core } from "@form-validation/bundle/full";
import React from "react";
import { actionEmitter, EmitEvent } from "../../config/ActionEmitter";
import styles from "./componentAdmin.module.scss";
import { createFormValidation, isFormValid } from "../../helpers/formValidationHelper";
import FilteredTable from "../../sites/components/common/FilteredTable";
import { CellProps, Column } from "react-table";

interface INewComponentModalProps {
  component: IComponent;
  show: boolean;
  onClose: () => void;
  onSave: (component: IComponent) => Promise<void>;
}

interface IPropertyDetailModalProps {
  property: IComponentProperty;
  propertyTypes: Array<IComponentPropertyType>;
  show: boolean;
  onClose: () => void;
  onSave: (property: IComponentProperty) => Promise<void>;
}

const NewComponentModal = ({ component, show, onClose, onSave }: INewComponentModalProps) => {
  const [draftComponent, setDraftComponent] = useState<IComponent>(component);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>): void => {
    const { name, value } = event.target;

    if (name) {
      setDraftComponent(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const createValidator = (): void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        Name: {
          validators: {
            notEmpty: { message: "component name is required"}
          }
        },
        DisplayName: {
          validators: {
            notEmpty: { message: "component display name is required"}
          }
        }
      });
    }
  };

  const handleSave = async (): Promise<void> => {
    if (fvRef.current) {
      const validres = await isFormValid(fvRef.current);

      if (validres) {
        await onSave(draftComponent);
      }
    }
  };

  useEffect(() => {
    if (show) {
      setDraftComponent(component);
      createValidator();
    } else {
      fvRef.current = null;
    }
  }, [component, show]);

  if (!show) {
    return null;
  }

  return (
    <AppModal
      onClose={onClose}
      showButtons={true}
      size="md"
      actionButtonVariant="success"
      cancelButtonVariant="primary"
      cancelButtonText="Cancel"
      dataActionButtonText="Save"
      actionButtonIcon={faSave}
      title="New Component"
      onDataAction={handleSave}>
      <Form id="NewComponentForm" ref={formRef}>
        <Row>
          <Col md={12} className="mt-3 fv-row">
            <FormLabel htmlFor="Name">Name (match to content template)</FormLabel>
            <FormControl
              type="text"
              value={draftComponent.Name}
              onChange={handleInputChange}
              title="component name"
              id="Name"
              name="Name"
            />
            <div className="fv-validation-message"></div>
          </Col>
        </Row>
        <Row>
          <Col md={12} className="mt-3 fv-row">
            <FormLabel htmlFor="DisplayName">Display name (visible in editor components)</FormLabel>
            <FormControl
              type="text"
              value={draftComponent.DisplayName}
              onChange={handleInputChange}
              title="component name"
              id="DisplayName"
              name="DisplayName"
            />
            <div className="fv-validation-message"></div>
          </Col>
        </Row>
      </Form>
    </AppModal>
  );
};

const PropertyDetailModal = ({ property, propertyTypes, show, onClose, onSave }: IPropertyDetailModalProps) => {
  const [draftProperty, setDraftProperty] = useState<IComponentProperty>(property);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const handlePropertyChange = (event: React.ChangeEvent<FormControlElement>): void => {
    const { name, value } = event.target;

    if (name) {
      setDraftProperty(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePropertyCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;

    if (name) {
      setDraftProperty(prev => ({
        ...prev,
        [name]: checked
      }));
    }
  };

  const handlePropertyTypeChange = (event: React.ChangeEvent<FormControlElement>): void => {
    const propid = event.target.value;

    if (!isNaN(Number(propid))) {
      const propType = propertyTypes.find(pt => pt.Id === Number(propid));

      if (propType) {
        setDraftProperty(prev => ({
          ...prev,
          Type: propType
        }));
      }
    }
  };

  const createValidator = (): void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        PropertyName: {
          validators: {
            notEmpty: { message: "property name is required" }
          }
        },
        DisplayLabel: {
          validators: {
            notEmpty: { message: "display label is required" }
          }
        }
      });
    }
  };

  const handleSave = async (): Promise<void> => {
    if (fvRef.current) {
      const validres = await isFormValid(fvRef.current);

      if (validres) {
        await onSave(draftProperty);
      }
    }
  };

  useEffect(() => {
    if (show) {
      setDraftProperty(property);
      createValidator();
    } else {
      fvRef.current = null;
    }
  }, [property, show]);

  if (!show) {
    return null;
  }

  return (
    <AppModal
      onClose={onClose}
      showButtons={true}
      size="lg"
      actionButtonVariant="success"
      cancelButtonVariant="primary"
      cancelButtonText="Cancel"
      dataActionButtonText="Save"
      actionButtonIcon={faSave}
      title="Property Detail"
      onDataAction={handleSave}>
      <Form id="ComponentPropertyForm" ref={formRef}>
        <Row>
          <Col md={12} lg={4} className="mt-3 fv-row">
            <FormLabel htmlFor="PropertyName">Name</FormLabel>
            <FormControl
              type="text"
              value={draftProperty.PropertyName ?? ""}
              onChange={handlePropertyChange}
              id="PropertyName"
              name="PropertyName"
              placeholder="property name"
              title="property name"
            />
            <div className="fv-validation-message"></div>
          </Col>
          <Col md={12} lg={4} className="mt-3 fv-row">
            <FormLabel htmlFor="DisplayLabel">Editor display label</FormLabel>
            <FormControl
              type="text"
              value={draftProperty.DisplayLabel ?? ""}
              onChange={handlePropertyChange}
              id="DisplayLabel"
              name="DisplayLabel"
              placeholder="property display label"
              title="property display label"
            />
            <div className="fv-validation-message"></div>
          </Col>
          <Col md={12} lg={4} className="mt-3">
            <FormLabel htmlFor="Type">Type</FormLabel>
            <FormSelect
              value={draftProperty.Type.Id}
              onChange={handlePropertyTypeChange}
              name="Type"
              id="Type"
              title="component property type">
              <option value="" disabled>Select property type...</option>
              {propertyTypes.map(pt => (
                <option key={`PT-${pt.Id}`} value={pt.Id}>{pt.Name}</option>
              ))}
            </FormSelect>
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={3} className="mt-3">
            <FormLabel htmlFor="UserEditable">Allow user edit</FormLabel>
            <FormCheckInput
              className="form-check"
              checked={draftProperty.UserEditable}
              title="allow edit"
              onChange={handlePropertyCheckChange}
              id="UserEditable"
              name="UserEditable"
            />
          </Col>
          <Col md={12} lg={3} className="mt-3">
            <FormLabel htmlFor="ShowOnExtendedTab">Extended/classes tab</FormLabel>
            <FormCheckInput
              className="form-check"
              checked={draftProperty.ShowOnExtendedTab ?? false}
              title="show on extended tab"
              onChange={handlePropertyCheckChange}
              id="ShowOnExtendedTab"
              name="ShowOnExtendedTab"
            />
          </Col>
          <Col md={12} lg={3} className="mt-3">
            <FormLabel htmlFor="UseFrameworkHelper">Use framework helper</FormLabel>
            <FormCheckInput
              className="form-check"
              checked={draftProperty.UseFrameworkHelper ?? false}
              title="show framework helper"
              onChange={handlePropertyCheckChange}
              id="UseFrameworkHelper"
              name="UseFrameworkHelper"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} className="mt-3">
            <FormLabel htmlFor="DefaultValue">Default value</FormLabel>
            <FormControl
              type="text"
              value={draftProperty.DefaultValue ?? ""}
              title="set default value"
              id="DefaultValue"
              name="DefaultValue"
              onChange={handlePropertyChange}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={6} className="mt-3">
            <FormLabel htmlFor="ValidatorRegEx">Validator expression</FormLabel>
            <FormControl
              type="text"
              value={draftProperty.ValidatorRegEx}
              onChange={handlePropertyChange}
              title="set a validation expression"
              id="ValidatorRegEx"
              name="ValidatorRegEx"
              placeholder="set a validation regular expression"
            />
          </Col>
          <Col md={12} lg={6} className="mt-3">
            <FormLabel htmlFor="ValidationErrorMessage">Validation message</FormLabel>
            <FormControl
              type="text"
              value={draftProperty.ValidationErrorMessage}
              onChange={handlePropertyChange}
              title="set a validation message"
              placeholder="set a validation message"
              name="ValidationErrorMessage"
              id="ValidationErrorMessage"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <hr />
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={2} className="mt-3">
            <FormLabel htmlFor="External">External map</FormLabel>
            <FormCheckInput
              className="form-check"
              checked={draftProperty.External}
              onChange={handlePropertyCheckChange}
              title="property maps external data"
              name="External"
              id="External"
            />
          </Col>
          <Col md={12} lg={10} className="mt-3">
            <FormLabel htmlFor="MapFromPropertyName">Source map property</FormLabel>
            <FormControl
              type="text"
              disabled={!draftProperty.External}
              value={draftProperty.MapFromPropertyName ?? ""}
              onChange={handlePropertyChange}
              title="set external property name"
              placeholder="set external property name for map"
              name="MapFromPropertyName"
              id="MapFromPropertyName"
            />
          </Col>
        </Row>
      </Form>
    </AppModal>
  );
};

const AllComponents = () => {
  const [components, setComponents] = useState<Array<IComponent>>([]);
  const [propertyTypes, setPropertyTypes] = useState<Array<IComponentPropertyType>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProperty, setSelectedProperty] = useState<IComponentProperty | null>(null);
  const [showPropertyDetail, setShowPropertyDetail] = useState<boolean>(false);
  const [newComponentDraft, setNewComponentDraft] = useState<IComponent | null>(null);
  const [showNewComponent, setShowNewComponent] = useState<boolean>(false);

  const sysAdmSvcRef = useRef<SystemAdminService | null>(null);
  const compFormRefs = useRef<React.RefObject<HTMLFormElement>[]>([]);
  const compFormFvRefs = useRef<Array<Core>>([]);

  const confirmation = useConfirm();

  const getComponents = async (): Promise<void> => {
    if (sysAdmSvcRef.current) {
      const comps = await sysAdmSvcRef.current.getAllComponents();
      compFormRefs.current = comps.map(() => React.createRef<HTMLFormElement>());
      setComponents(comps);
    }
  };

  const getPropertyTypes = async (): Promise<void> => {
    if (sysAdmSvcRef.current) {
      const types = await sysAdmSvcRef.current.getComponentPropertyTypes();
      setPropertyTypes(types);
    }
  };

  const editComponentProperty = (prop: IComponentProperty): void => {
    if (prop) {
      setSelectedProperty(prop);
      setShowPropertyDetail(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedProperty, (item) => item.Component?.Id, () => closePropertyDetail());

  const deleteComponent = async (comp: IComponent, event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    if (comp && sysAdmSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${comp.DisplayName}</b>?<br />This will remove the component from the content component menu.<br /><b>This action cannot be undone.</b>`, "Delete Component");

      if (confirmed) {
        const delres = await sysAdmSvcRef.current.deleteComponent(comp.Id);
        if (delres) {
          setComponents(prev => prev.filter(c => c.Id !== comp.Id));
          closeDeletedSelection(comp.Id);
        }
      }
    }
  };

  const renderPropertySection = (
    comp: IComponent,
    properties: IComponentProperty[],
    heading: string,
    description: string,
    emptyMessage: string,
    eventKey: string,
    showOnExtendedTabDefault: boolean
  ): React.ReactNode => {
    const propertyColumns: Column<IComponentProperty>[] = [
      {
        Header: "Label",
        accessor: (row) => row.DisplayLabel || row.PropertyName,
        id: "DisplayLabel"
      },
      {
        Header: "Property Name",
        accessor: "PropertyName"
      },
      {
        Header: "Type",
        accessor: (row) => row.Type?.Name || "Not set",
        id: "TypeName"
      },
      {
        id: "Actions",
        Header: "",
        Cell: ({ row }: CellProps<IComponentProperty>) => (
          <div className={styles.propertyActions}>
            <Button
              variant="outline-primary"
              title="edit property"
              className="btn-nomin"
              onClick={() => editComponentProperty(row.original)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="outline-secondary"
              title="move up"
              className="btn-nomin"
              onClick={() => moveProperty(comp.Id, row.original.Id, "up")}>
              <FontAwesomeIcon icon={faSortUp} />
            </Button>
            <Button
              variant="outline-secondary"
              title="move down"
              className="btn-nomin"
              onClick={() => moveProperty(comp.Id, row.original.Id, "down")}>
              <FontAwesomeIcon icon={faSortDown} />
            </Button>
            <Button
              variant="outline-danger"
              title="remove property"
              className="btn-nomin"
              onClick={() => handleComponentPropertyDelete(comp.Id, row.original.Id)}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        )
      }
    ];

    return (
      <Accordion className={styles.propertyAccordion}>
        <AccordionItem eventKey={eventKey}>
          <AccordionHeader>{heading}</AccordionHeader>
          <AccordionBody>
            <div className={styles.propertySection}>
              <div className={styles.propertySectionHeader}>
                <div>
                  <p className={styles.sectionText}>{description}</p>
                </div>
                <Button
                  variant="light"
                  className={styles.secondaryButton}
                  title={`add ${heading.toLowerCase()}`}
                  onClick={() => handleComponentPropertyAdd(comp.Id, showOnExtendedTabDefault)}>
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add property</span>
                </Button>
              </div>
              {properties.length > 0 ? (
                <div className={styles.propertyTableSection}>
                  <FilteredTable<IComponentProperty>
                    columns={propertyColumns}
                    data={properties}
                    noDataMessage={emptyMessage}
                    containerClass={styles.propertyTableContainer}
                    showFilter={false}
                    showPagerBottom={false}
                    showPagerTop={false}
                    pageSize={100}
                  />
                </div>
              ) : (
                <Alert variant="light" className={`${styles.emptyState} text-center fw-bold`}>
                  <span>{emptyMessage}</span>
                </Alert>
              )}
            </div>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    );
  };

  const handleComponentChange = (compid: number, event: React.ChangeEvent<FormControlElement>): void => {
    if (compid) {
      const comp = components.find(c => c.Id === compid);
      if (comp) {
        const { name, value } = event.target;
        if (name) {
          setComponents(prevComponents =>
            prevComponents.map(comp =>
              comp.Id === compid
                ? {
                  ...comp,
                  [name]: value, // Dynamically update the property
                }
                : comp
            )
          );
        }
      }
    }
  };

  const handleComponentCheckChange = (compid: number, event: React.ChangeEvent<HTMLInputElement>): void => {
    if (compid) {
      const comp = components.find(c => c.Id === compid);
      if (comp) {
        const { name, checked } = event.target;
        if (name) {
          setComponents(prevComponents =>
            prevComponents.map(comp =>
              comp.Id === compid
                ? {
                  ...comp,
                  [name]: checked, // Dynamically update the property
                }
                : comp
            )
          );
        }
      }
    }
  };

  const handleComponentPropertyDelete = async (compid: number, propid: number): Promise<void> => {
    if (sysAdmSvcRef.current && compid && propid) {
      const confirmed = await confirmation.requestConfirmation("Remove the property from the component?<br /><b>This action cannot be undone.</b>", "Delete Property");

      if (confirmed) {
        const delres = await sysAdmSvcRef.current.deleteProperty(propid);

        if (delres) {
          setComponents(prevComponents =>
            prevComponents.map(comp => {
              if (comp.Id === compid) {
                // Return a new object for the modified component with updated Properties
                return {
                  ...comp,
                  Properties: comp.Properties.filter(p => p.Id !== propid),
                };
              }
              return comp; // Return the unchanged component
            })
          );
        }
      }
    }
  };

  const moveProperty = async (componentId: number, propertyId: number, direction: "up" | "down"): Promise<void> => {
    if (!sysAdmSvcRef.current) {
      return;
    }

    const moved = direction === "up"
      ? await sysAdmSvcRef.current.movePropertyUp(componentId, propertyId)
      : await sysAdmSvcRef.current.movePropertyDown(componentId, propertyId);

    if (!moved) {
      return;
    }

    setComponents((prevComponents) =>
      prevComponents.map((component) => {
        if (component.Id !== componentId) {
          return component;
        }

        const currentIndex = component.Properties.findIndex((property) => property.Id === propertyId);
        const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= component.Properties.length) {
          return component;
        }

        const reordered = [...component.Properties];
        [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];

        return {
          ...component,
          Properties: reordered
        };
      })
    );
  };

  const handleAddComponent = (): void => {
    if (sysAdmSvcRef.current) {
      setNewComponentDraft(sysAdmSvcRef.current.newComponent());
      setShowNewComponent(true);
    }
  };

  const handleComponentPropertyAdd = (compid: number, showOnExtendedTabDefault: boolean = false): void => {
    if (compid) {
      const comp = components.find(c => c.Id === compid);
      const type = propertyTypes[0];

      if (comp && type) {
        const newprop: IComponentProperty = {
          DefaultValue: "",
          DisplayLabel: "",
          EditorDisplayOrder: 0,
          External: false,
          MapFromPropertyName: "",
          PropertyName: "",
          Id: 0,
          PublishedPropertyValueId: 0,
          PublishedValue: "",
          ShowOnExtendedTab: showOnExtendedTabDefault,
          UseFrameworkHelper: false,
          UserEditable: true,
          Type: propertyTypes[0],
          ValidationErrorMessage: "",
          ValidatorRegEx: "",
          Component: comp
        };

        setSelectedProperty(newprop);
        setShowPropertyDetail(true);
      }
    }
  };

  const closePropertyDetail = (): void => {
    setShowPropertyDetail(false);
    setSelectedProperty(null);
  };

  const closeNewComponent = (): void => {
    setShowNewComponent(false);
    setNewComponentDraft(null);
  };

  const saveProperty = async (propertyToSave: IComponentProperty): Promise<void> => {
    if (!propertyToSave || !sysAdmSvcRef.current) {
      return;
    }

    const response = await sysAdmSvcRef.current.saveComponentProperty(propertyToSave);
  
    if (response && response.Component) {
      const { Component: updatedComponent, Id: responsePropertyId } = response;
  
      // Update the component with the new or updated property
      setComponents(prevComponents =>
        prevComponents.map(comp =>
          comp.Id === updatedComponent.Id
            ? {
                ...comp,
                Properties: comp.Properties.some(prop => prop.Id === responsePropertyId)
                  ? comp.Properties.map(prop =>
                      prop.Id === responsePropertyId ? response : prop // Update existing property
                    )
                  : [...comp.Properties, response], // Add new property
              }
            : comp
        )
      );
  
      // Close the property detail modal
      closePropertyDetail();
    }
  };

  const saveComponent = async (compid: number): Promise<void> => {
    if (compid && sysAdmSvcRef.current) {
      // Find the component in the state
      const comp = components.find(c => c.Id === compid);

      if (comp) {
        // Find the corresponding form validation instance
        const formValidationInstance = compFormFvRefs.current.find((fv, index) => {
          return components[index]?.Id === compid;
        });

        if (formValidationInstance) {
          // Perform validation
          const status = await isFormValid(formValidationInstance);

          if (status) {
            // Validation passed, proceed to save
            const res = await sysAdmSvcRef.current.saveComponent(comp);
            if (res) {
              // Update state with the saved component
              setComponents(prev => prev.map(c => (c.Id === res.Id ? res : c)));
              actionEmitter.emit(EmitEvent.Result, true, "Component details saved");
            }
          } else {
            console.log("Validation failed.");
          }
        } else {
          console.log("Form validation instance not found for component:", compid);
        }
      }
    }
  };

  const saveNewComponent = async (componentToSave: IComponent) : Promise<void> => {
    if (sysAdmSvcRef.current) {
      const res = await sysAdmSvcRef.current.saveComponent(componentToSave);

      if (res) {
        setComponents(prev => [
          ...prev,
          res
        ]);

        closeNewComponent();
      }
    }
  };

  useEffect(() => {
    // Skip if forms are not loaded
    if (loading || compFormRefs.current.length === 0) return;

    // Clear previous validation instances
    compFormFvRefs.current.forEach(fvInstance => {
      if (fvInstance?.destroy) {
        fvInstance.destroy(); // Properly destroy the instance
      }
    });
    compFormFvRefs.current = [];

    // Initialize validation for each form
    compFormRefs.current.forEach((formRef, index) => {
      if (formRef.current) {
        const fvInstance = createFormValidation(formRef.current, {
          Name: {
            validators: {
              notEmpty: { message: "Component name is required" },
            },
          },
          DisplayName: {
            validators: {
              notEmpty: { message: "Component display name is required" },
            },
          }
        });

        // Store the instance in compFormFvRefs
        compFormFvRefs.current[index] = fvInstance;
      }
    });

    // Cleanup when component unmounts or dependencies change
    return () => {
      compFormFvRefs.current.forEach(fvInstance => {
        if (fvInstance?.destroy) {
          fvInstance.destroy(); // Destroy validation instances
        }
      });
      compFormFvRefs.current = [];
    };
  }, [loading]);

  useEffect(() => {
    if (!sysAdmSvcRef.current) {
      sysAdmSvcRef.current = new SystemAdminService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getComponents(),
          getPropertyTypes()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    getData();

  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading component details..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={`${styles.page} ${styles.componentsPage}`}>
              <section className={`app-list-hero ${styles.systemSettingsHero}`}>
                <div>
                  <p className="app-list-hero__eyebrow">System Administration</p>
                  <h1 className="app-list-hero__title">Components</h1>
                  <p className="app-list-hero__summary">
                    Manage the reusable building blocks available across the editor, including
                    their external data mapping and the content properties each one exposes.
                  </p>
                </div>
                <Button
                  variant="primary"
                  className={styles.primaryButton}
                  title="add new component"
                  onClick={handleAddComponent}>
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add component</span>
                </Button>
              </section>
          {components.length > 0 ? (
            <>
              <div className={styles.componentList}>
                {components.map((comp, index) => (
                  (() => {
                    const contentProperties = comp.Properties?.filter(prop => !prop.ShowOnExtendedTab) ?? [];
                    const classStyleProperties = comp.Properties?.filter(prop => prop.ShowOnExtendedTab) ?? [];

                    return (
                  <Accordion className={styles.componentCard} key={`component-${comp.Id}-${comp.Name}`}>
                    <AccordionItem eventKey={String(comp.Id)}>
                    <AccordionHeader>
                      <div className={styles.componentHeader}>
                        <div className={styles.componentHeaderTitle}>
                          <span className={styles.componentName}>{comp.DisplayName}</span>
                          <span className={styles.componentMeta}>
                            {comp.Name}
                            {comp.ExternalData ? " • External data enabled" : " • Local component"}
                          </span>
                        </div>
                        <span className={styles.componentPill}>
                          {comp.Properties?.length ?? 0} {(comp.Properties?.length ?? 0) === 1 ? "property" : "properties"}
                        </span>
                      </div>
                    </AccordionHeader>
                    <AccordionBody className={styles.componentBody}>
                      <Form id={`ComponentForm-${comp.Id}`} ref={compFormRefs.current[index]}>
                        <Row>
                          <Col md={12} lg={6} className="mt-3 fv-row">
                            <FormLabel htmlFor="Name">Name</FormLabel>
                            <FormControl
                              type="text"
                              value={comp.Name}
                              onChange={(e) => handleComponentChange(comp.Id, e)}
                              placeholder="component name"
                              title="component name"
                              id="Name"
                              name="Name"
                            />
                            <div className="fv-validation-message"></div>
                          </Col>
                          <Col md={12} lg={6} className="mt-3 fv-row">
                            <FormLabel htmlFor="DisplayName">Display name</FormLabel>
                            <FormControl
                              type="text"
                              value={comp.DisplayName}
                              onChange={(e) => handleComponentChange(comp.Id, e)}
                              placeholder="component display name"
                              title="component display name"
                              id="DisplayName"
                              name="DisplayName"
                            />
                            <div className="fv-validation-message"></div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12} lg={2} className="mt-3">
                            <FormLabel htmlFor="ExternalData">External data</FormLabel>
                            <FormCheckInput
                              className="form-check"
                              checked={comp.ExternalData}
                              title="externally mapped data"
                              name="ExternalData"
                              id="ExternalData"
                              onChange={(e) => handleComponentCheckChange(comp.Id, e)}
                            />
                          </Col>
                          <Col md={12} lg={10} className="mt-3">
                            <FormLabel htmlFor="ExternalSource">External data source Url</FormLabel>
                            <FormControl
                              type="text"
                              value={comp.ExternalSource ?? ""}
                              title="url for external data source"
                              disabled={!comp.ExternalData}
                              placeholder="url for external data source"
                              name="ExternalSource"
                              id="ExternalSource"
                              onChange={(e) => handleComponentChange(comp.Id, e)}
                            />
                          </Col>
                        </Row>
                      </Form>
                      <Row>
                        <Col md={12}>
                          <hr />
                        </Col>
                      </Row>
                      {renderPropertySection(
                        comp,
                        contentProperties,
                        "Content Properties",
                        "These fields appear on the main content tab when editors configure this component.",
                        "No content properties found for this component yet.",
                        "content-properties",
                        false
                      )}
                      <Row>
                        <Col md={12}>
                          <hr />
                        </Col>
                      </Row>
                      {renderPropertySection(
                        comp,
                        classStyleProperties,
                        "Classes & Style Properties",
                        "These fields appear on the extended or classes tab for styling, classes, and advanced presentation options.",
                        "No classes or style properties found for this component yet.",
                        "classes-style-properties",
                        true
                      )}
                      <Row>
                        <Col md={12}>
                          <hr />
                        </Col>
                      </Row>
                      <div className={styles.componentFooter}>
                        <div className={styles.footerMeta}>
                          Keep the component name aligned with the content template and only enable external
                          data when the mapping source is stable.
                        </div>
                        <div className={styles.footerActions}>
                          <Button
                            variant="light"
                            className={styles.dangerButton}
                            title="delete component"
                            onClick={(e) => deleteComponent(comp, e)}>
                            <FontAwesomeIcon icon={faTimes} />
                            <span>Delete</span>
                          </Button>
                          <Button
                            variant="primary"
                            className={styles.primaryButton}
                            title="save component"
                            onClick={() => saveComponent(comp.Id)}>
                            <FontAwesomeIcon icon={faSave} />
                            <span>Save changes</span>
                          </Button>
                        </div>
                      </div>
                    </AccordionBody>
                    </AccordionItem>
                  </Accordion>
                    );
                  })()
                ))}
              </div>
            </>
          )
            :
            (
              <Alert variant="light" className={`${styles.pageEmptyState} p-4 text-center`}>
                <span className="fw-bold">No components configured</span>
              </Alert>
            )}
        </div>
      )}

      {selectedProperty && (
        <PropertyDetailModal
          property={selectedProperty}
          propertyTypes={propertyTypes}
          show={showPropertyDetail}
          onClose={closePropertyDetail}
          onSave={saveProperty}
        />
      )}

      {newComponentDraft && (
        <NewComponentModal
          component={newComponentDraft}
          show={showNewComponent}
          onClose={closeNewComponent}
          onSave={saveNewComponent}
        />
      )}

    </>
  );
}

export default AllComponents;
